import { DiscoveryApi, FetchApi, createApiRef } from '@backstage/core-plugin-api';

export interface ApiProxyKey {
  id: string;
  team: string;
  label: string;
  key_suffix: string;
  scopes: string;
  is_revoked: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  user_entity_ref?: string;
}

export interface ApiKeyUsageStats {
  total_requests: number;
  total_bytes_sent: number;
  total_bytes_received: number;
  avg_duration_ms: number;
  last_request: string | null;
}

export interface ApiKeyUsageResponse {
  key: {
    suffix: string;
    team: string;
    label: string;
    owner: string;
  };
  usage: ApiKeyUsageStats;
}

export interface GitHubMembershipStatus {
  username: string;
  isMember: boolean;
  cached: boolean;
  error?: string;
  checkedAt?: string; // ISO timestamp of when membership was checked
}

export interface CreateKeyRequest {
  team: string;
  label: string;
  expiresAt?: string;
  scopes?: string;
}

export interface CreateKeyResponse {
  key: string;
  id: string;
  team: string;
  label: string;
  suffix: string;
  createdAt: string;
}

export type ApiKeyRequestStatus = 'pending' | 'approved' | 'denied';

export interface ApiKeyAccessRequest {
  id: string;
  user_entity_ref: string;
  user_email: string | null;
  team: string;
  label: string;
  scope: string;
  reason: string | null;
  status: ApiKeyRequestStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  consumed_at: string | null;
  validity_days: number | null;
  revoked_at: string | null;
  created_at: string;
}

export interface CreateAccessRequest {
  team: string;
  label: string;
  scope: string;
  reason?: string;
  validityDays?: number;
}

export interface ApiProxyApi {
  listKeys(): Promise<{ items: ApiProxyKey[] }>;
  createKey(request: CreateKeyRequest): Promise<CreateKeyResponse>;
  revokeKey(id: string): Promise<{ ok: boolean }>;
  listAllKeys(limit?: number, offset?: number): Promise<{ items: ApiProxyKey[] }>;
  getKeyUsage(suffix: string): Promise<ApiKeyUsageResponse>;
  checkAdmin(): Promise<{ isAdmin: boolean }>;
  revokeKeyAdmin(id: string): Promise<{ ok: boolean }>;
  checkGitHubMembership(username: string, forceRefresh?: boolean): Promise<GitHubMembershipStatus>;
  checkGitHubMemberships(usernames: string[]): Promise<{ results: GitHubMembershipStatus[] }>;
  requestKeyAccess(request: CreateAccessRequest): Promise<{ id: string; scope: string; status: string; createdAt: string }>;
  listMyKeyRequests(): Promise<{ items: ApiKeyAccessRequest[] }>;
  listPendingKeyRequests(): Promise<{ items: ApiKeyAccessRequest[] }>;
  listUsableApprovals(): Promise<{ items: ApiKeyAccessRequest[] }>;
  approveKeyRequest(id: string): Promise<{ id: string; status: string }>;
  denyKeyRequest(id: string): Promise<{ id: string; status: string }>;
  revokeApproval(id: string): Promise<{ id: string; status: string; revoked_at: string }>;
}

export const apiProxyApiRef = createApiRef<ApiProxyApi>({
  id: 'plugin.api-proxy.service',
});

export class ApiProxyClient implements ApiProxyApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return `${await this.discoveryApi.getBaseUrl('api-proxy')}/management/v1`;
  }

  async listKeys(): Promise<{ items: ApiProxyKey[] }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/keys`);
    if (!response.ok) {
      throw new Error(`Failed to list keys: ${response.statusText}`);
    }
    return await response.json();
  }

  async createKey(request: CreateKeyRequest): Promise<CreateKeyResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/keys`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({} as any));
      // The backend returns either { error: { message } } (error middleware) or
      // { error: string }; normalize both so approval / active-key reasons surface.
      const message =
        (body as any)?.error?.message ??
        (body as any)?.error ??
        (body as any)?.message;
      throw new Error(
        message || `Failed to create key: ${response.statusText}`,
      );
    }
    return await response.json();
  }

  async revokeKey(id: string): Promise<{ ok: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/keys/${id}/revoke`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to revoke key: ${response.statusText}`);
    }
    return await response.json();
  }

  async listAllKeys(limit?: number, offset?: number): Promise<{ items: ApiProxyKey[] }> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.append('limit', String(limit));
    }
    if (offset !== undefined) {
      params.append('offset', String(offset));
    }
    const url = `${baseUrl}/admin/keys${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to list all keys: ${response.statusText}`);
    }
    return await response.json();
  }

  async getKeyUsage(suffix: string): Promise<ApiKeyUsageResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admin/usage/${suffix}`);
    if (!response.ok) {
      throw new Error(`Failed to get key usage: ${response.statusText}`);
    }
    return await response.json();
  }

  async checkAdmin(): Promise<{ isAdmin: boolean }> {
    const baseUrl = await this.getBaseUrl();
    // Try to call admin endpoint - if it succeeds, user is admin
    // If it fails with 403, user is not admin
    try {
      const response = await this.fetchApi.fetch(`${baseUrl}/admin/keys?limit=1`);
      if (response.ok) {
        return { isAdmin: true };
      }
      if (response.status === 403) {
        return { isAdmin: false };
      }
      // Other errors - treat as not admin for safety
      return { isAdmin: false };
    } catch {
      return { isAdmin: false };
    }
  }

  async revokeKeyAdmin(id: string): Promise<{ ok: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admin/keys/${id}/revoke`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to revoke key: ${response.statusText}`);
    }
    return await response.json();
  }

  async checkGitHubMembership(username: string, forceRefresh: boolean = false): Promise<GitHubMembershipStatus> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/admin/github-membership/${encodeURIComponent(username)}${forceRefresh ? '?forceRefresh=true' : ''}`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to check GitHub membership: ${response.statusText}`);
    }
    return await response.json();
  }

  async checkGitHubMemberships(usernames: string[]): Promise<{ results: GitHubMembershipStatus[] }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admin/github-memberships`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ usernames }),
    });
    if (!response.ok) {
      throw new Error(`Failed to check GitHub memberships: ${response.statusText}`);
    }
    return await response.json();
  }

  async requestKeyAccess(
    request: CreateAccessRequest,
  ): Promise<{ id: string; scope: string; status: string; createdAt: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({} as any));
      // The backend returns either { error: { message } } (error middleware) or
      // { error: string }; normalize both so we never render '[object Object]'.
      const message =
        (body as any)?.error?.message ??
        (body as any)?.error ??
        (body as any)?.message;
      throw new Error(
        message || `Failed to submit request: ${response.statusText}`,
      );
    }
    return await response.json();
  }

  async listMyKeyRequests(): Promise<{ items: ApiKeyAccessRequest[] }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/requests`);
    if (!response.ok) {
      throw new Error(`Failed to list requests: ${response.statusText}`);
    }
    return await response.json();
  }

  async listPendingKeyRequests(): Promise<{ items: ApiKeyAccessRequest[] }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admin/requests`);
    if (!response.ok) {
      throw new Error(`Failed to list pending requests: ${response.statusText}`);
    }
    return await response.json();
  }

  async listUsableApprovals(): Promise<{ items: ApiKeyAccessRequest[] }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admin/requests?status=approved`);
    if (!response.ok) {
      throw new Error(`Failed to list usable approvals: ${response.statusText}`);
    }
    return await response.json();
  }

  async approveKeyRequest(id: string): Promise<{ id: string; status: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admin/requests/${id}/approve`, {
      method: 'POST',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({} as any));
      // The backend returns either { error: { message } } (error middleware) or
      // { error: string }; normalize both so review reasons (e.g. already
      // approved/denied) surface instead of a generic status text.
      const message =
        (body as any)?.error?.message ??
        (body as any)?.error ??
        (body as any)?.message;
      throw new Error(
        message || `Failed to approve request: ${response.statusText}`,
      );
    }
    return await response.json();
  }

  async denyKeyRequest(id: string): Promise<{ id: string; status: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admin/requests/${id}/deny`, {
      method: 'POST',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({} as any));
      // The backend returns either { error: { message } } (error middleware) or
      // { error: string }; normalize both so review reasons (e.g. already
      // approved/denied) surface instead of a generic status text.
      const message =
        (body as any)?.error?.message ??
        (body as any)?.error ??
        (body as any)?.message;
      throw new Error(
        message || `Failed to deny request: ${response.statusText}`,
      );
    }
    return await response.json();
  }

  async revokeApproval(id: string): Promise<{ id: string; status: string; revoked_at: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admin/requests/${id}/revoke`, {
      method: 'POST',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({} as any));
      const message =
        (body as any)?.error?.message ??
        (body as any)?.error ??
        (body as any)?.message;
      throw new Error(
        message || `Failed to revoke approval: ${response.statusText}`,
      );
    }
    return await response.json();
  }
}